import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { useTranslation } from "react-i18next";
import type { Draft, IntensityKind } from "@jackedlogs/shared";

import type { ConfirmFn } from "../../seams";
import type { useTimelineLogger } from "../../Logger/useTimelineLogger";
import {
  applyPrescribeAxis,
  applyPrescribeBroadcast,
  applyPrescribeNote,
  applyPrescribeWorkKind,
  resolvePrescribeAddr,
  type PrescribeAddrCtx,
} from "./prescribeDispatch";

/** PE-22 K3.2/K3.3/K3.4 — the bound set's prescription commands beyond reps
 *  and effort (which usePrescribeMount keeps, next to `dispatch` and
 *  `effort`): the load-axis selector, "apply to all sets", and the "A
 *  tempo"/"Nota" chips. Split out of usePrescribeMount.ts (K3.4 landed it
 *  exactly at the 500-line cap) — a pure extraction, same addressing
 *  discipline (resolved from the live draft at write time, never a captured
 *  index) as every other prescribeDispatch caller. */
export function usePrescribeSetCommands(
  setDraft: Dispatch<SetStateAction<Draft>>,
  addrCtx: PrescribeAddrCtx,
  tl: ReturnType<typeof useTimelineLogger>,
  confirm: ConfirmFn,
  t: ReturnType<typeof useTranslation>["t"],
) {
  // K3.2 — the LoadAxisSelector's write: same addressing as onPrescribeEffort
  // (resolved to the program-Draft address at tap time, never a captured
  // index). null while no set is bound — the selector itself is not
  // rendered then (see `loadAxis` below).
  const onPrescribeAxis = useCallback(
    (kind: IntensityKind) =>
      setDraft((d) =>
        applyPrescribeAxis(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
          kind,
        ),
      ),
    [setDraft, addrCtx, tl.activeEntryIdx, tl.boundSetIdx],
  );
  const loadAxis =
    tl.boundSet === null
      ? null
      : {
          kind: tl.boundSet.intensityPrescribed?.kind ?? ("none" as const),
          onSelect: onPrescribeAxis,
        };

  // K3.3 — "Apply to all sets": broadcasts the bound row's own prescription
  // onto every set row of the exercise. null (nothing to broadcast) while no
  // set is bound.
  //
  // Review fix (PE-22 K3 triad, Major a11y): this OVERWRITES every other set
  // row's prescription with no way back, so — same as every other
  // destructive action in the app — it asks first through the shared
  // ConfirmFn seam. Fire-and-forget, same shape as useDiscardWorkout: a
  // dismissed dialog resolves false (ConfirmFn's contract) and the broadcast
  // never runs.
  const onApplyToAllSets = useCallback(() => {
    void confirm({
      title: t("mobile.logger.applyToAllSets", {
        defaultValue: "Apply to all sets",
      }),
      body: t("mobile.logger.applyToAllConfirm", {
        defaultValue: "Overwrite every set with this set's prescription?",
      }),
      confirmLabel: t("mobile.workouts.maxBumpConfirm", {
        defaultValue: "Confirm",
      }),
      cancelLabel: t("mobile.common.cancel", { defaultValue: "Cancel" }),
      destructive: true,
    }).then((ok) => {
      if (!ok) return;
      setDraft((d) =>
        applyPrescribeBroadcast(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
        ),
      );
    });
  }, [confirm, t, setDraft, addrCtx, tl.activeEntryIdx, tl.boundSetIdx]);

  // K3.4 — "A tempo": the reps⇄time toggle. Seeds from the exercise's own
  // default when turning on, exactly like the live logger's start-timer
  // affordance seeds a countdown (buildTimerStartHandlers) — never a bare
  // "30" when the catalog already names this exercise's bout length.
  const onToggleTimed = useCallback(() => {
    const timedNow = tl.boundSet?.workPrescribed?.kind === "countdown";
    const seed = String(tl.activeExercise?.defaultDurationSec ?? 30);
    setDraft((d) =>
      applyPrescribeWorkKind(
        d,
        resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
        !timedNow,
        seed,
      ),
    );
  }, [
    setDraft,
    addrCtx,
    tl.activeEntryIdx,
    tl.boundSetIdx,
    tl.boundSet,
    tl.activeExercise,
  ]);

  // K3.4 — "Nota": writes the row's per-week note as the coach types.
  const onChangeNote = useCallback(
    (text: string) =>
      setDraft((d) =>
        applyPrescribeNote(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
          text,
        ),
      ),
    [setDraft, addrCtx, tl.activeEntryIdx, tl.boundSetIdx],
  );

  return {
    loadAxis,
    // null while no set is bound, exactly like loadAxis above.
    onApplyToAllSets: tl.boundSet === null ? null : onApplyToAllSets,
    // undefined while no set is bound — FocusPanelChipRow reads the
    // callback's presence as the chip's visibility, same convention
    // `onSplitGroup`/`onCreateGroup` already use on FocusPanelProps.
    timedChip:
      tl.boundSet === null
        ? undefined
        : {
            active: tl.boundSet.workPrescribed?.kind === "countdown",
            onToggle: onToggleTimed,
          },
    noteChip:
      tl.boundSet === null
        ? undefined
        : {
            active: (tl.boundSet.notePrescribed ?? "").trim() !== "",
            text: tl.boundSet.notePrescribed ?? "",
            onChange: onChangeNote,
          },
  };
}
